import { useDispatch, useSelector } from 'react-redux';
import { authSliceAPI, clearAllSlices } from '../store/authSlice';
import { useCallback, useEffect, useRef } from 'react';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { redactSensitive } from '../../../shared/utils/logSanitization';
import {
    initializeSessionCheck,
    initializeAuthListener,
    cleanupAuthListener,
} from '../services/sessionService';

// Re-export for backward compatibility
export { cleanupAuthListener };

const logger = createLogger('useAuth.js');

export const useAuth = () => {
    const dispatch = useDispatch();
    const { accessToken, refreshToken, loading, error, sessionId, userId, loyalpassAdmin } = useSelector(
        (state) => state.auth
    );
    const hasInitialized = useRef(false);

    // Initial session check when the component mounts (only once globally)
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            initializeSessionCheck(dispatch, authSliceAPI.checkSession);
        }
    }, [dispatch]);

    // Set up the auth state listener once globally
    useEffect(() => {
        const cleanup = initializeAuthListener(dispatch, authSliceAPI.checkSession);
        // Return cleanup function if provided
        return cleanup || undefined;
    }, [dispatch]);

    const handleLogin = useCallback(
        async (loginFormData) => {
            logger.info('Login process started in useAuth hook', redactSensitive(loginFormData));
            try {
                await dispatch(authSliceAPI.login(loginFormData)).unwrap();
                logger.info('Login dispatch completed successfully in useAuth hook');
            } catch (error) {
                const message = extractErrorMessage(error);
                logger.error('Login failed in useAuth hook', message);
                throw new Error(message);
            }
        },
        [dispatch]
    );

    const handleLogout = useCallback(async () => {
        logger.info('Logout process started in useAuth hook)');
        try {
            await dispatch(authSliceAPI.logout()).unwrap();
            // Clear all slices after successful logout
            dispatch(clearAllSlices());
            logger.info('Logout dispatch completed successfully in useAuth hook');
        } catch (error) {
            const message = extractErrorMessage(error);
            logger.error('Logout failed in useAuth hook', message);
            throw new Error(message);
        }
    }, [dispatch]);

    const handleRegister = useCallback(
        async (RegisterFormData) => {
            logger.info('Sign up process started in useAuth hook', redactSensitive(RegisterFormData));
            try {
                const data = await dispatch(authSliceAPI.register(RegisterFormData)).unwrap();
                logger.info('Signup dispatch completed successfully', data);
                return data; // Return successful registration data
            } catch (error) {
                const message = extractErrorMessage(error);
                logger.error('Register failed in useAuth hook', message);
                throw new Error(message);
            }
        },
        [dispatch]
    );

    const handleResetPassword = useCallback(
        async (ResetPasswordFormData) => {
            logger.info('Reset password process started in useAuth hook', {
                email: ResetPasswordFormData.email,
            });
            try {
                const data = await dispatch(
                    authSliceAPI.resetPassword(ResetPasswordFormData)
                ).unwrap();
                logger.info('Reset password dispatch completed successfully', data);
                return data;
            } catch (error) {
                const message = extractErrorMessage(error);
                logger.error('Reset password failed in useAuth hook', message);
                throw new Error(message);
            }
        },
        [dispatch]
    );

    const handleUpdatePassword = useCallback(
        async (UpdatePasswordFormData) => {
            logger.info('Update password process started in useAuth hook');
            try {
                const data = await dispatch(
                    authSliceAPI.updatePassword(UpdatePasswordFormData)
                ).unwrap();
                logger.info('Update password dispatch completed successfully', data);
                return data;
            } catch (error) {
                const message = extractErrorMessage(error);
                logger.error('Update password failed in useAuth hook', message);
                throw new Error(message);
            }
        },
        [dispatch]
    );

    return {
        state: {
            accessToken,
            refreshToken,
            loading,
            error,
            sessionId,
            userId,
            loyalpassAdmin,
        },
        isAuthenticated: !!accessToken,

        // Actions
        login: handleLogin,
        logout: handleLogout,
        register: handleRegister,
        resetPassword: handleResetPassword,
        updatePassword: handleUpdatePassword,
    };
};
